import { Select } from '@/components/Controls/Select';
import { ImageUpload } from '@/components/FileUpload/ImageUpload';
import { FormField } from '@/components/Form/FormField';
import { FormManagementProps } from '@/type/form.types';
import { ErrorResponse } from '@/utils/axios.http';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { WatermarkPosition } from '@/api/entities/group';

interface GroupCheckPointWatermarkProps<
  TSchema extends FieldValues,
> extends FormManagementProps<TSchema> {
  checkPointWatermarkField: string;
  checkPointWatermarkPositionField: string;
  mutateUpdateGroup?: UseMutateFunction<
    AxiosResponse<TSchema, unknown>,
    ErrorResponse,
    TSchema,
    unknown
  >;
  mutateUploadImage?: UseMutateFunction<
    AxiosResponse<unknown, unknown>,
    ErrorResponse,
    FormData,
    unknown
  >;
}

export const GroupCheckPointWatermark = <TSchema extends FieldValues>({
  t,
  checkPointWatermarkField,
  checkPointWatermarkPositionField,
  data,
  watch,
  setValue,
  submitDisabled,
  mutateUpdateGroup,
  mutateUploadImage,
}: GroupCheckPointWatermarkProps<TSchema>) => {
  const checkPointWatermarkPositionValue =
    watch && watch(checkPointWatermarkPositionField as Path<TSchema>);

  // Prepare watermark horizontal position
  const horizontalPosition =
    checkPointWatermarkPositionValue === WatermarkPosition.CENTER
      ? WatermarkPosition.CENTER
      : checkPointWatermarkPositionValue?.split('_')[1];

  // Prepare watermark vertical position
  const verticalPosition = checkPointWatermarkPositionValue?.split('_')[0];

  return (
    <div className="mt-5">
      <div className="flex flex-wrap flex-1 items-center gap-8 max-md:gap-3">
        <FormField
          containerClassName="flex-1 gap-5 max-1.5xl:gap-1.5 max-md:flex-none max-md:w-full"
          textOuterWrapperClassName="w-auto max-1.5xl:w-full"
          text={
            <>
              <span className="font-medium">
                {t('common:horizontalPosition')}
              </span>
              :
            </>
          }
          // required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Select
              placeholder={t('common:chooseTheHorizontalPosition')}
              value={horizontalPosition}
              // errorMessage={
              //   !horizontalPosition && t('watermarkHorizontalPositionRequired')
              // }
              onChange={(e) => {
                const position = `${verticalPosition || 'BOTTOM'}_${
                  e.target.value
                }`;

                setValue(
                  checkPointWatermarkPositionField as Path<TSchema>,
                  position as PathValue<TSchema, Path<TSchema>>,
                );

                mutateUpdateGroup({
                  ...data,
                  groupCustomImage: {
                    ...data?.groupCustomImage,
                    [checkPointWatermarkPositionField]:
                      position === 'CENTER_CENTER' ||
                      position === 'CENTER_LEFT' ||
                      position === 'CENTER_RIGHT'
                        ? WatermarkPosition.CENTER
                        : position,
                  },
                });
              }}
              options={[
                {
                  label: t('common:left'),
                  value: 'LEFT',
                },
                {
                  label: t('common:center'),
                  value: 'CENTER',
                },
                {
                  label: t('common:right'),
                  value: 'RIGHT',
                },
              ]}
            />
          </div>
        </FormField>
        <div className="h-[20px] w-[1px] border-r border-r-gray-400 max-1.5xl:hidden" />
        <FormField
          containerClassName="flex-1 gap-5 max-1.5xl:gap-1.5 max-md:flex-none max-md:w-full"
          textOuterWrapperClassName="w-auto max-1.5xl:w-full"
          text={
            <>
              <span className="font-medium">
                {t('common:verticalPosition')}
              </span>
              :
            </>
          }
          // required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Select
              placeholder={t('common:chooseTheVerticalPosition')}
              value={verticalPosition}
              // errorMessage={
              //   !verticalPosition && t('watermarkVerticalPositionRequired')
              // }
              onChange={(e) => {
                const position = `${e.target.value}_${
                  horizontalPosition || 'RIGHT'
                }`;

                setValue(
                  checkPointWatermarkPositionField as Path<TSchema>,
                  position as PathValue<TSchema, Path<TSchema>>,
                );

                mutateUpdateGroup({
                  ...data,
                  groupCustomImage: {
                    ...data?.groupCustomImage,
                    [checkPointWatermarkPositionField]:
                      position === 'CENTER_CENTER' ||
                      position === 'CENTER_LEFT' ||
                      position === 'CENTER_RIGHT'
                        ? WatermarkPosition.CENTER
                        : position,
                  },
                });
              }}
              options={[
                {
                  label: t('common:top'),
                  value: 'TOP',
                },
                {
                  label: t('common:center'),
                  value: 'CENTER',
                },
                {
                  label: t('common:bottom'),
                  value: 'BOTTOM',
                },
              ]}
            />
          </div>
        </FormField>
      </div>
      <div className="mt-5">
        <ImageUpload
          t={t}
          chooseAnotherImgContainerClassName="mt-6"
          allowedFormats={['png']}
          disabledChooseAnotherImgButton={submitDisabled}
          mutateUploadImage={mutateUploadImage}
          initialImage={
            data?.groupCustomImage?.[checkPointWatermarkField] &&
            `${R2_PUBLIC_DOMAIN}/images/group/${data?.id}/${data?.groupCustomImage?.[checkPointWatermarkField]}`
          }
        />
      </div>
    </div>
  );
};
